import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import "./ContactPage.css";
import Navbar from "../../components/Navbar/Navbar";
import Footer from "../../components/Footer/Footer";
import { submitWebLead } from "../../api/webLeads";
import { WEB_LEAD_MODULES } from "../../constants/webLeadModules";
import { formatContactInternalComments } from "../../utils/formatLeadComments";

const ContactPage = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    const t = setTimeout(() => setVisible(true), 80);
    return () => clearTimeout(t);
  }, []);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Name is required";
    if (!form.email.trim()) e.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = "Enter a valid email";
    if (!form.subject.trim()) e.subject = "Subject is required";
    if (!form.message.trim()) e.message = "Message is required";
    return e;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
    if (errors[name]) setErrors((p) => ({ ...p, [name]: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }

    setSubmitError("");
    setSubmitting(true);
    try {
      await submitWebLead({
        fullName: form.name.trim(),
        email: form.email.trim(),
        phone: (form.phone || "").replace(/\s/g, "") || "NA",
        module: WEB_LEAD_MODULES.CONTACT,
        internalComments: formatContactInternalComments({
          form,
          leadModule: WEB_LEAD_MODULES.CONTACT,
        }),
      });
      setSubmitted(true);
    } catch (err) {
      setSubmitError(
        err?.message ||
          "We could not send your message. Please try again or email support@wealthcompounders.in.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    setErrors({});
    setSubmitted(false);
    setSubmitError("");
  };

  return (
    <div className="ct-page">
      {/* Ambient orbs */}
      <div className="ct-orb ct-orb--1" />
      <div className="ct-orb ct-orb--2" />
      <div className="ct-orb ct-orb--3" />

      {/* Site Navbar */}
      <Navbar />

      <main className={`ct-main ${visible ? "ct-visible" : ""}`}>
        {/* Page header */}
        <div className="ct-header">
          <div className="ct-badge">
            <span className="ct-badge-dot" />
            Get In Touch
          </div>
          <h1 className="ct-title">
            Let's Talk <span className="ct-title-accent">Finance</span>
          </h1>
          <p className="ct-subtitle">
            Have a question about your investments or our services? We're here
            to help. Reach out and our team will get back to you within 24
            hours.
          </p>
        </div>

        <div className="ct-body">
          {/* ── LEFT: Info panel ── */}
          <aside className="ct-info">
            <div className="ct-info-card">
              <div className="ct-info-top">
                <h2 className="ct-info-title">Contact Information</h2>
                <p className="ct-info-tagline">
                  We'd love to hear from you. Drop us a line anytime.
                </p>
              </div>

              <div className="ct-info-items">
                <div className="ct-info-item">
                  <div className="ct-info-icon">
                    <i className="fas fa-phone-alt" />
                  </div>
                  <div>
                    <span className="ct-info-label">Phone</span>
                    <a href="tel:9314507135" className="ct-info-value">
                      +91 93145 07135
                    </a>
                  </div>
                </div>

                <div className="ct-info-item">
                  <div className="ct-info-icon">
                    <i className="fas fa-envelope" />
                  </div>
                  <div>
                    <span className="ct-info-label">Email</span>
                    <a
                      href="mailto:support@wealthcompounders.in"
                      className="ct-info-value"
                    >
                      support@wealthcompounders.in
                    </a>
                  </div>
                </div>

                <div className="ct-info-item">
                  <div className="ct-info-icon">
                    <i className="fas fa-clock" />
                  </div>
                  <div>
                    <span className="ct-info-label">Business Hours</span>
                    <span className="ct-info-value">
                      Mon – Sat, 9 AM – 6 PM IST
                    </span>
                  </div>
                </div>
              </div>

              <div className="ct-info-divider" />

              <div className="ct-info-note">
                <i className="fas fa-shield-alt" />
                <p>
                  Your information is secure and will never be shared with third
                  parties.
                </p>
              </div>

              {/* Decorative circles */}
              <div className="ct-info-deco-a" />
              <div className="ct-info-deco-b" />
            </div>
          </aside>

          {/* ── RIGHT: Form ── */}
          <section className="ct-form-wrap">
            <form className="ct-form" onSubmit={handleSubmit} noValidate>
              <div className="ct-form-row">
                <div
                  className={`ct-field ${errors.name ? "ct-field--err" : ""}`}
                >
                  <label className="ct-label" htmlFor="name">
                    Full Name <span className="ct-req">*</span>
                  </label>
                  <div className="ct-input-wrap">
                    <i className="fas fa-user ct-input-icon" />
                    <input
                      id="name"
                      name="name"
                      type="text"
                      className="ct-input"
                      placeholder="Your full name"
                      value={form.name}
                      onChange={handleChange}
                      autoComplete="name"
                    />
                  </div>
                  {errors.name && (
                    <span className="ct-err-msg">{errors.name}</span>
                  )}
                </div>

                <div
                  className={`ct-field ${errors.email ? "ct-field--err" : ""}`}
                >
                  <label className="ct-label" htmlFor="email">
                    Email Address <span className="ct-req">*</span>
                  </label>
                  <div className="ct-input-wrap">
                    <i className="fas fa-envelope ct-input-icon" />
                    <input
                      id="email"
                      name="email"
                      type="email"
                      className="ct-input"
                      placeholder="you@example.com"
                      value={form.email}
                      onChange={handleChange}
                      autoComplete="email"
                    />
                  </div>
                  {errors.email && (
                    <span className="ct-err-msg">{errors.email}</span>
                  )}
                </div>
              </div>

              <div className="ct-form-row">
                <div className="ct-field">
                  <label className="ct-label" htmlFor="phone">
                    Phone Number
                  </label>
                  <div className="ct-input-wrap">
                    <i className="fas fa-phone-alt ct-input-icon" />
                    <input
                      id="phone"
                      name="phone"
                      type="tel"
                      className="ct-input"
                      placeholder="+91 XXXXX XXXXX"
                      value={form.phone}
                      onChange={handleChange}
                      autoComplete="tel"
                    />
                  </div>
                </div>

                <div
                  className={`ct-field ${errors.subject ? "ct-field--err" : ""}`}
                >
                  <label className="ct-label" htmlFor="subject">
                    Subject <span className="ct-req">*</span>
                  </label>
                  <div className="ct-input-wrap">
                    <i className="fas fa-tag ct-input-icon" />
                    <select
                      id="subject"
                      name="subject"
                      className="ct-input ct-select"
                      value={form.subject}
                      onChange={handleChange}
                    >
                      <option value="">Select a topic</option>
                      <option value="Investment Advisory">
                        Investment Advisory
                      </option>
                      <option value="Mutual Funds">Mutual Funds</option>
                      <option value="Portfolio Review">Portfolio Review</option>
                      <option value="NJ Wealth">
                        NJ Wealth
                      </option>
                      <option value="General Enquiry">General Enquiry</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  {errors.subject && (
                    <span className="ct-err-msg">{errors.subject}</span>
                  )}
                </div>
              </div>

              <div
                className={`ct-field ${errors.message ? "ct-field--err" : ""}`}
              >
                <label className="ct-label" htmlFor="message">
                  Message <span className="ct-req">*</span>
                </label>
                <div className="ct-input-wrap">
                  <i className="fas fa-comment-alt ct-input-icon ct-input-icon--ta" />
                  <textarea
                    id="message"
                    name="message"
                    className="ct-input ct-textarea"
                    placeholder="Tell us how we can help you..."
                    rows={5}
                    value={form.message}
                    onChange={handleChange}
                  />
                </div>
                {errors.message && (
                  <span className="ct-err-msg">{errors.message}</span>
                )}
              </div>

              {submitError && (
                <p className="ct-err-msg ct-err-msg--form">{submitError}</p>
              )}

              <button
                type="submit"
                className="ct-submit-btn"
                disabled={submitting}
              >
                <span>{submitting ? "Sending…" : "Send Message"}</span>
                <i className="fas fa-paper-plane" />
              </button>
            </form>
          </section>
        </div>
      </main>

      {/* Site Footer */}
      <Footer />

      {/* ── SUCCESS POPUP ── */}
      {submitted && (
        <div className="ct-overlay" onClick={handleReset}>
          <div className="ct-popup" onClick={(e) => e.stopPropagation()}>
            <div className="ct-popup-icon">
              <div className="ct-popup-ring" />
              <i className="fas fa-check" />
            </div>
            <h2 className="ct-popup-title">Message Sent!</h2>
            <p className="ct-popup-text">
              Thank you, <strong>{form.name}</strong>. We've received your
              message and will get back to you at <strong>{form.email}</strong>{" "}
              within 24 hours.
            </p>
            <div className="ct-popup-actions">
              <button
                className="ct-popup-btn ct-popup-btn--primary"
                onClick={handleReset}
              >
                Send Another
              </button>
              <Link to="/" className="ct-popup-btn ct-popup-btn--outline">
                Back to Home
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactPage;
